import { useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import {
  useMaturityAssessments,
  useAllMaturityAssessments,
  useMaturityFrameworks,
  useMaturityDistribution,
  type MaturityDistributionVariables,
} from '../../graphql/queries';
import { MaturityAssessmentStatus, MaturitySubjectEntityType } from '../../graphql/types';
import type {
  MaturityTrendPoint,
  MaturityAssessment,
  MaturityAssessmentFilter,
} from '../../graphql/types';
import {
  MaturityTrendChart,
  MaturityDistributionChart,
  type TrendSeries,
} from '../../components/maturity';
import { DetailSkeleton, EmptyState } from '../../components/shared';
import '@tylertech/forge/card';
import '@tylertech/forge/select';
import './$serviceId.assessments.trends.css';

export const Route = createFileRoute('/services/$serviceId/assessments/trends')({
  component: ServiceAssessmentTrendsPage,
});

export { ServiceAssessmentTrendsPage };

// Colorblind-safe palette matching MaturitySpiderChart SECTOR_COLORS
const AREA_COLORS = ['#1f77b4', '#ff7f0e', '#2ca02c', '#d62728', '#9467bd'];
const OVERALL_COLOR = '#8c564b';

function buildTrendSeries(
  assessments: MaturityAssessment[],
  framework: { areas: Array<{ id: string; name: string }> }
): TrendSeries[] {
  // Only Approved assessments, sorted by period
  const approved = [...assessments]
    .filter((a) => a.status === MaturityAssessmentStatus.Approved)
    .sort((a, b) => a.assessmentPeriod.localeCompare(b.assessmentPeriod));

  if (approved.length < 2) return [];

  const overallSeries: TrendSeries = {
    id: 'overall',
    label: 'Overall',
    color: OVERALL_COLOR,
    points: approved.map((a): MaturityTrendPoint => ({
      assessmentId: a.entityId,
      assessmentPeriod: a.assessmentPeriod,
      overallScore: a.overallScore ?? 0,
      areaScores: a.areaScores ?? [],
    })),
    scoreAccessor: (p) => p.overallScore,
  };

  const areaSeries: TrendSeries[] = framework.areas.map((area, idx) => ({
    id: area.id,
    label: area.name,
    color: AREA_COLORS[idx % AREA_COLORS.length],
    points: approved.map((a): MaturityTrendPoint => ({
      assessmentId: a.entityId,
      assessmentPeriod: a.assessmentPeriod,
      overallScore: a.overallScore ?? 0,
      areaScores: a.areaScores ?? [],
    })),
    // NOTE (#1603): `?? 0` here used to mask a real bug — `assessments` could
    // contain rows from a *different* framework than the one that built this
    // series, which has no matching area IDs at all, so every point silently
    // plotted as a false zero. That cross-framework case is now impossible:
    // `assessments` is fetched scoped to `framework`'s own frameworkId below.
    // The fallback stays only for the legitimate, same-framework case where a
    // capability in this area was never responded to, so no score was ever
    // computed for it (see areaScoresMap in backend MaturityAssessment.ts) —
    // confirmed still exercised by existing fixtures with incomplete
    // areaScores; removing it entirely regresses that case.
    scoreAccessor: (p) => {
      const found = p.areaScores.find((s) => s.areaId === area.id);
      return found?.score ?? 0;
    },
  }));

  return [overallSeries, ...areaSeries];
}

/** The frameworkId of the assessment with the latest `assessmentPeriod`. */
function mostRecentFrameworkId(assessments: MaturityAssessment[]): string | undefined {
  if (assessments.length === 0) return undefined;
  const sorted = [...assessments].sort((a, b) =>
    a.assessmentPeriod.localeCompare(b.assessmentPeriod)
  );
  return sorted[sorted.length - 1]?.frameworkId;
}

function ServiceAssessmentTrendsPage(): JSX.Element {
  const { serviceId } = Route.useParams();
  const CardElement = 'forge-card' as React.ElementType;
  const ForgeSelect = 'forge-select' as React.ElementType;
  const ForgeOption = 'forge-option' as React.ElementType;

  // Explicit user override of the framework selector below; undefined until
  // the user picks one, so the default (most-recent-assessment's framework)
  // applies until they change it.
  const [selectedFrameworkId, setSelectedFrameworkId] = useState<string | undefined>(undefined);

  // Discovery fetch — every assessment for this service, unscoped by
  // framework, used only to find the distinct set of frameworks present and
  // to default the selector to the most-recently-assessed framework (#1603).
  // Walks every page (see `useAllMaturityAssessments`) rather than capping at
  // one — a service with 50+ assessments could otherwise have a second
  // framework's rows sort past the first page, hiding both the selector and
  // that framework's history entirely (review finding on #1615).
  const {
    data: discoveryData,
    loading: discoveryLoading,
    error: assessmentsError,
  } = useAllMaturityAssessments(
    { subjectEntityType: MaturitySubjectEntityType.Service, subjectEntityId: serviceId },
    50
  );

  const discoveredAssessments = discoveryData?.maturityAssessments?.edges?.map((e) => e.node) ?? [];
  const distinctFrameworkIds = Array.from(new Set(discoveredAssessments.map((a) => a.frameworkId)));
  const hasMultipleFrameworks = distinctFrameworkIds.length > 1;
  const defaultFrameworkId = mostRecentFrameworkId(discoveredAssessments);
  const effectiveFrameworkId = selectedFrameworkId ?? defaultFrameworkId ?? '';

  // Once a service has assessments under 2+ frameworks, the rows used to
  // build the trend chart/distribution must be re-fetched scoped to just the
  // selected framework via the server-side filter — filtering the (up to 50)
  // discovery rows client-side after the fact could silently drop legitimate
  // rows for the selected framework if the mixed-framework sample truncated
  // at 50 first (the same truncation-cap defect class as #1348 elsewhere).
  const scopedFilter: MaturityAssessmentFilter = {
    subjectEntityType: MaturitySubjectEntityType.Service,
    subjectEntityId: serviceId,
    frameworkId: effectiveFrameworkId,
  };
  const { data: scopedData, loading: scopedLoading } = useMaturityAssessments(
    scopedFilter,
    50,
    !hasMultipleFrameworks || !effectiveFrameworkId
  );

  const assessments = hasMultipleFrameworks
    ? (scopedData?.maturityAssessments?.edges?.map((e) => e.node) ?? [])
    : discoveredAssessments;

  const { data: frameworksData, loading: frameworksLoading } = useMaturityFrameworks(50);
  const frameworks = frameworksData?.maturityFrameworks?.edges?.map((e) => e.node) ?? [];
  const framework = frameworks.find((f) => f.entityId === effectiveFrameworkId);

  const distVars: MaturityDistributionVariables = { frameworkId: effectiveFrameworkId };
  const { data: distData, loading: distLoading } = useMaturityDistribution(
    distVars,
    !effectiveFrameworkId
  );

  if (discoveryLoading || frameworksLoading || scopedLoading || distLoading) {
    return (
      <div className="assessment-trends-page">
        <DetailSkeleton cards={2} />
      </div>
    );
  }

  if (assessmentsError) {
    return (
      <div className="assessment-trends-page">
        <EmptyState
          icon="error"
          title="Failed to load assessments"
          description={assessmentsError.message}
        />
      </div>
    );
  }

  const approvedCount = assessments.filter(
    (a) => a.status === MaturityAssessmentStatus.Approved
  ).length;

  const series = framework ? buildTrendSeries(assessments, framework) : [];
  const distribution = distData?.maturityDistributionByFramework;

  return (
    <div className="assessment-trends-page">
      <div className="page-header">
        <div className="page-header-left">
          <Link to="/services/$serviceId/assessments" params={{ serviceId }} className="back-link">
            ← Assessments
          </Link>
          <h1 className="forge-typography--headline5">Maturity Trends</h1>
        </div>
      </div>

      {hasMultipleFrameworks && (
        <div className="assessment-trends-page__framework-selector">
          <ForgeSelect
            name="frameworkId"
            label="Framework"
            value={effectiveFrameworkId}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
              setSelectedFrameworkId(e.target.value)
            }
          >
            {distinctFrameworkIds.map((id) => {
              const fw = frameworks.find((f) => f.entityId === id);
              return (
                <ForgeOption key={id} value={id}>
                  {fw ? `${fw.name} v${fw.version}` : id}
                </ForgeOption>
              );
            })}
          </ForgeSelect>
        </div>
      )}

      {/* Trend chart */}
      <CardElement>
        <div className="assessment-trends-page__section">
          <h2 className="forge-typography--subtitle1 assessment-trends-page__section-title">
            Score Progression
          </h2>

          {approvedCount < 2 ? (
            <EmptyState
              icon="timeline"
              title="Not enough data"
              description="At least 2 approved assessments are required to show a trend. Complete and approve more assessments to track progress over time."
            />
          ) : (
            <>
              <MaturityTrendChart series={series} width={640} height={320} />
              {series.length > 0 && (
                <ul className="assessment-trends-page__series-legend" aria-label="Series legend">
                  {series.map((s) => (
                    <li key={s.id}>
                      <span
                        className="assessment-trends-page__legend-swatch"
                        style={{ background: s.color }}
                        aria-hidden="true"
                      />
                      {s.label}
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </div>
      </CardElement>

      {/* Portfolio distribution */}
      {distribution && (
        <CardElement>
          <div className="assessment-trends-page__section">
            <h2 className="forge-typography--subtitle1 assessment-trends-page__section-title">
              Portfolio Distribution
            </h2>
            <p className="forge-typography--body2">
              Distribution of all assessed subjects across maturity levels for this framework (
              {distribution.totalSubjects} subject{distribution.totalSubjects !== 1 ? 's' : ''}, avg{' '}
              {distribution.averageScore.toFixed(1)}).
            </p>
            <MaturityDistributionChart
              levelCounts={distribution.levelCounts}
              averageScore={distribution.averageScore}
              totalSubjects={distribution.totalSubjects}
              width={480}
              height={260}
            />
          </div>
        </CardElement>
      )}
    </div>
  );
}
